import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';

// API Response format from Laravel backend
export interface ApiResponse<T = unknown> {
  CODE: number;
  MESSAGE: string;
  RESULT: T;
}

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000,
  // Accept 2xx status codes and custom API codes like 450 (empty result)
  validateStatus: (status) => status >= 200 && status < 500,
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => {
    // Check for 401 in success handler since validateStatus accepts 4xx codes
    if (response.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      window.location.href = '/signin';
    }
    return response;
  },
  (error: AxiosError<ApiResponse>) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      window.location.href = '/signin';
    } else {
      // Report network errors, CORS failures, timeouts, and 5xx to Sentry
      Sentry.captureException(error, {
        tags: {
          type: error.response ? 'server_error' : 'network_error',
          status: error.response?.status,
          url: error.config?.url,
        },
      });
    }
    return Promise.reject(error);
  }
);

export default api;
